import { useEffect, useState } from "preact/hooks"
import { useFetch } from "../hooks/useFetch"
import type { KidProfile, MatchSummary, Topic, User } from "../types"
import { LevelPicker } from "./LevelPicker"
import { LoadError } from "./LoadError"
import {
	abandonSoloPlay,
	acceptMatch,
	createMatch,
	createSoloPlay,
	declineMatch,
	fetchActiveSoloPlays,
	fetchFriends,
	fetchMatches,
	fetchProfiles,
	fetchRecentSoloPlays,
	fetchTopics,
	forfeitMatch,
	switchProfile,
} from "./lib/api"
import { Modal } from "./Modal"
import { OnlineDot } from "./OnlineDot"
import { isQuotaError, ProModal, usePlan, whenPhrase } from "./Plan"
import { SiteFooter, TopicRequestLine } from "./SiteFooter"
import { TopicIcon } from "./TopicIcons"
import { TopicList } from "./TopicList"

interface HomeProps {
	me: User
	navigate: (path: string) => void
	// Called after this session has been switched into a kid profile, so App
	// can re-read the session and swap the whole shell for kid mode.
	onProfileChanged: () => void
}

function otherPlayer(m: MatchSummary, meID: number): User {
	return m.challenger.id === meID ? m.opponent : m.challenger
}

// The unfinished game an abandon confirmation is asking about. A solo round is
// simply thrown away; a match is forfeited, which hands the opponent the win —
// so the two need different words and the target carries who that opponent is.
type AbandonTarget =
	| { kind: "solo"; id: number; topic: string }
	| { kind: "match"; id: number; topic: string; opponent: string }

export function Home({ me, navigate, onProfileChanged }: HomeProps) {
	// Every fetched section below keeps its own error next to its own data
	// (useFetch): three states each — loading (data null, no error), failed
	// (error set), loaded (data non-null) — because a shared `error` string
	// cannot say *which* list is missing, and a section that only knows "null"
	// cannot tell "still loading" from "gave up".
	const {
		data: topics,
		error: topicsError,
		reload: loadTopics,
	} = useFetch(fetchTopics, "Couldn't load the topics.")
	const {
		data: solo,
		error: soloError,
		reload: loadRecentSolo,
	} = useFetch(fetchRecentSoloPlays, "Couldn't load your recent games.")
	const {
		data: activeSolo,
		error: activeSoloError,
		reload: loadActiveSolo,
		setData: setActiveSolo,
	} = useFetch(fetchActiveSoloPlays, "Failed to load rounds")
	// The kids' profiles, for the hand-over row. An account with none has no
	// section at all, so a null list is "not here yet", never "none".
	const {
		data: profiles,
		error: profilesError,
		reload: loadProfiles,
	} = useFetch(fetchProfiles, "Couldn't load your profiles.")
	// True while a switch is in flight — the whole app is about to be replaced,
	// so the buttons only need to stop taking a second tap.
	const [handingOver, setHandingOver] = useState(false)
	// Never swallow the friends fetch. The challenge picker's empty state says
	// "You have no friends yet", and rendering that because a request failed
	// tells a player with friends something false about their own account.
	// `friends` stays null on failure and the error state speaks for itself.
	const {
		data: friends,
		error: friendsError,
		reload: loadFriends,
		refresh: refreshFriends,
	} = useFetch(fetchFriends, "Couldn't load your friends.")
	const {
		data: matches,
		error: matchesError,
		refresh: refreshMatches,
		setData: setMatches,
	} = useFetch(fetchMatches, "Couldn't load your matches.")
	// Page-level errors from *actions* (start a round, decline a challenge) —
	// loading failures belong to their section, not here.
	const [error, setError] = useState("")
	const [activeTopic, setActiveTopic] = useState<Topic | null>(null)
	// Which difficulty step the modal will start. Topics without levels
	// ignore it and send 1.
	const [level, setLevel] = useState(1)
	const [starting, setStarting] = useState(false)
	// Which unfinished game the abandon confirmation is open for, if any.
	// Abandoning is destructive — a solo round is deleted, a match is handed to
	// the opponent — so the row's button only ever opens this dialog.
	const [abandoning, setAbandoning] = useState<AbandonTarget | null>(null)
	const [abandonBusy, setAbandonBusy] = useState(false)
	// The id of the incoming challenge currently being accepted, if any — the
	// pair of buttons on that row only needs to stop taking a second tap.
	const [accepting, setAccepting] = useState<number | null>(null)
	// The free cap, for the one-line counter under "Pick a topic". Refetched
	// on every mount, which is every return from a round.
	const { plan, error: planError, reload: reloadPlan } = usePlan()
	// The Pro dialog: null closed, otherwise the sentence to lead with (the
	// server's 402 body when a round was refused, "" from the Go Pro link).
	const [proReason, setProReason] = useState<string | null>(null)

	// Friends refresh on their own 30s beat — slower than the matches poll
	// below, since the only thing that moves here is the online dots and
	// presence itself is only accurate to a couple of minutes. Both polls are
	// background refreshes: the list stays up while the next answer is on its
	// way, and a failed poll leaves it up too.
	useEffect(() => {
		const interval = window.setInterval(refreshFriends, 30000)
		return () => window.clearInterval(interval)
	}, [refreshFriends])

	// Poll the matches list every 5s so incoming challenges and opponents'
	// moves show up without a manual refresh. Cleared on unmount.
	useEffect(() => {
		const interval = window.setInterval(refreshMatches, 5000)
		return () => window.clearInterval(interval)
	}, [refreshMatches])

	// Hand the phone over: the switch is per session, so the very next render
	// of the app is the kid's. Nothing is updated in place here — App re-reads
	// the session and swaps the shell.
	async function handOver(p: KidProfile) {
		setHandingOver(true)
		setError("")
		try {
			await switchProfile(p.id)
			onProfileChanged()
		} catch (e) {
			setError(e instanceof Error ? e.message : `Couldn't switch to ${p.name}.`)
			setHandingOver(false)
		}
	}

	async function playSolo(topic: Topic) {
		setStarting(true)
		setError("")
		try {
			const state = await createSoloPlay(topic.id, level)
			navigate(`/solo/${state.id}`)
		} catch (e) {
			if (isQuotaError(e)) {
				refusedRound(e.message)
			} else {
				setError(e instanceof Error ? e.message : "Failed to start solo play")
			}
			setStarting(false)
		}
	}

	async function challengeFriend(topic: Topic, friend: User) {
		setStarting(true)
		setError("")
		try {
			const state = await createMatch(topic.id, level, friend.id)
			navigate(`/match/${state.id}`)
		} catch (e) {
			if (isQuotaError(e)) {
				refusedRound(e.message)
			} else {
				setError(e instanceof Error ? e.message : "Failed to send challenge")
			}
			setStarting(false)
		}
	}

	// A round refused for the free cap (402) swaps the topic dialog for the
	// Pro one, leading with the server's own sentence, and refreshes the
	// counter, which the refusal has just proven stale.
	function refusedRound(reason: string) {
		setActiveTopic(null)
		setProReason(reason)
		reloadPlan()
	}

	// Accepting actually starts the match — the accept call itself, not a bare
	// navigation that would just show the Accept button again on the play page.
	async function acceptChallenge(id: number) {
		setError("")
		setAccepting(id)
		try {
			await acceptMatch(id)
			navigate(`/match/${id}`)
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to accept challenge")
			setAccepting(null)
		}
	}

	async function declineChallenge(id: number) {
		setError("")
		try {
			await declineMatch(id)
			setMatches((prev) =>
				prev
					? { ...prev, incoming: prev.incoming.filter((m) => m.id !== id) }
					: prev,
			)
		} catch (e) {
			setError(
				e instanceof Error ? e.message : "Failed to respond to challenge",
			)
		}
	}

	// Drop the game the confirmation dialog is about. Both lists are updated in
	// place rather than refetched: the matches poll is 5s away and the solo list
	// has no poll at all, so waiting for one would leave the row the player just
	// abandoned sitting on screen.
	async function confirmAbandon() {
		const target = abandoning
		if (!target) return
		setAbandonBusy(true)
		setError("")
		try {
			if (target.kind === "solo") {
				await abandonSoloPlay(target.id)
				setActiveSolo((prev) =>
					prev ? prev.filter((a) => a.id !== target.id) : prev,
				)
			} else {
				await forfeitMatch(target.id)
				setMatches((prev) =>
					prev
						? { ...prev, active: prev.active.filter((m) => m.id !== target.id) }
						: prev,
				)
			}
			setAbandoning(null)
		} catch (e) {
			setError(
				e instanceof Error
					? e.message
					: target.kind === "solo"
						? "Failed to abandon the round"
						: "Failed to forfeit the match",
			)
			setAbandoning(null)
		} finally {
			setAbandonBusy(false)
		}
	}

	async function cancelChallenge(id: number) {
		setError("")
		try {
			await declineMatch(id)
			setMatches((prev) =>
				prev
					? { ...prev, outgoing: prev.outgoing.filter((m) => m.id !== id) }
					: prev,
			)
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to cancel challenge")
		}
	}

	return (
		<div class="overflow-auto h-100">
			<div class="container page">
				{error && <div class="alert danger">{error}</div>}

				{/* An in-progress round is the strongest thing to keep playing, so it
				    sits above the topic list. Leaving a round mid-way (the board's
				    Leave button) does not throw it away — it stays active and shows
				    up here to pick back up exactly where you left off. An empty list
				    is the absence of the block; a failed fetch says so instead. */}
				{activeSoloError && (
					<LoadError message={activeSoloError} onRetry={loadActiveSolo} />
				)}
				{activeSolo && activeSolo.length > 0 && (
					<div class="stack gap-3">
						<h2>Resume your rounds</h2>
						{/* The row is a div wrapping a link, not one big <a>: the
						    abandon button cannot live inside an anchor. The link still
						    covers everything except that button. */}
						<div class="list">
							{activeSolo.map((a) => (
								<div class="flex between center gap-2" key={a.id}>
									{/* Two lines, not one: on a phone the single sentence wrapped
									    mid-phrase ("score" on its own line). */}
									<a href={`/solo/${a.id}`} class="plain grow stack">
										<span class="fw-semibold">{a.topic_name}</span>
										<span class="muted small tnum">
											Question {a.current_question + 1} of {a.total} · score{" "}
											{a.score}
										</span>
									</a>
									<button
										type="button"
										class="link danger sm"
										onClick={() =>
											setAbandoning({
												kind: "solo",
												id: a.id,
												topic: a.topic_name,
											})
										}
									>
										Abandon
									</button>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Hand-over row. An account with no profiles gets no heading at
				    all — the feature does not exist until there is someone to hand
				    the phone to. Loading shows nothing; a failed fetch says so
				    rather than quietly implying there are none. */}
				{profilesError ? (
					<div class="stack gap-3">
						<h2>Hand the phone to…</h2>
						<LoadError message={profilesError} onRetry={loadProfiles} />
					</div>
				) : profiles && profiles.length > 0 ? (
					<div class="stack gap-3">
						<h2>Hand the phone to…</h2>
						<div class="flex wrap center gap-2">
							{profiles.map((p) => (
								<button
									type="button"
									key={p.id}
									class="outline"
									disabled={handingOver}
									onClick={() => handOver(p)}
								>
									{p.name}
								</button>
							))}
							<a href="/me" class="small ml-1">
								Manage profiles
							</a>
						</div>
					</div>
				) : null}

				<div class="stack gap-3">
					<h2>Pick a topic</h2>
					{/* The free cap, one quiet line. Loading shows nothing; a failed
					    fetch shows nothing either (the cap is enforced server-side,
					    and a missing counter is not worth a red bar over the topics);
					    only a plan that actually arrived gets to state a number. */}
					{!planError && plan && plan.tier === "free" && (
						<p class="muted small">
							<span class="tnum">
								{plan.rounds_left} of {plan.rounds_limit}
							</span>{" "}
							free rounds left today
							{plan.rounds_left === 0 && plan.resets_at
								? `, next one ${whenPhrase(plan.resets_at)}`
								: ""}
							{" · "}
							<button
								type="button"
								class="link"
								onClick={() => setProReason("")}
							>
								Go Pro
							</button>
						</p>
					)}
					{/* An error ends the loading state. Showing the alert *and*
					    leaving the spinner running turned the whole home page into
					    a permanent spinner with a red box over it. */}
					{topicsError ? (
						<LoadError message={topicsError} onRetry={loadTopics} />
					) : !topics ? (
						<div class="spinner sm" role="status" />
					) : (
						<TopicList
							topics={topics}
							onSelect={(t) => {
								setActiveTopic(t)
								setLevel(1)
							}}
						/>
					)}
					<TopicRequestLine />
				</div>

				{/* One line for the whole matches block: all four sections below
				    come from the same poll, so four copies of the same message
				    would only be noise. The poll retries every 5s on its own, so
				    there is nothing to offer a Retry button for. */}
				{!matches && matchesError && <LoadError message={matchesError} />}

				<div class="grid-2">
					<div class="stack gap-2">
						<h3>Incoming challenges</h3>
						{!matches ? (
							// Failed, so stop spinning — the line above says why.
							matchesError ? null : (
								<div class="spinner sm" role="status" />
							)
						) : matches.incoming.length === 0 ? (
							<p class="muted small">No pending challenges.</p>
						) : (
							<div class="list">
								{matches.incoming.map((m) => (
									<div class="flex between center gap-2" key={m.id}>
										<span>
											<strong>{m.challenger.username}</strong> challenged you —{" "}
											{m.topic_name}
										</span>
										<span class="flex gap-2">
											<button
												type="button"
												class="success sm"
												disabled={accepting === m.id}
												onClick={() => acceptChallenge(m.id)}
											>
												Accept
											</button>
											<button
												type="button"
												class="sm"
												disabled={accepting === m.id}
												onClick={() => declineChallenge(m.id)}
											>
												Decline
											</button>
										</span>
									</div>
								))}
							</div>
						)}
					</div>

					<div class="stack gap-2">
						<h3>Your pending challenges</h3>
						{!matches ? null : matches.outgoing.length === 0 ? (
							<p class="muted small">Nothing pending.</p>
						) : (
							<div class="list">
								{matches.outgoing.map((m) => (
									<div class="flex between center gap-2" key={m.id}>
										<span>
											Waiting for <strong>{m.opponent.username}</strong> —{" "}
											{m.topic_name}
										</span>
										<button
											type="button"
											class="sm"
											onClick={() => cancelChallenge(m.id)}
										>
											Cancel
										</button>
									</div>
								))}
							</div>
						)}
					</div>

					<div class="stack gap-2">
						<h3>Active matches</h3>
						{!matches ? null : matches.active.length === 0 ? (
							<p class="muted small">No matches in progress.</p>
						) : (
							<div class="list">
								{matches.active.map((m) => {
									const opp = otherPlayer(m, me.id)
									const myScore = m.scores?.[String(me.id)] ?? 0
									const oppScore = m.scores?.[String(opp.id)] ?? 0
									return (
										<div class="flex between center gap-2" key={m.id}>
											<a href={`/match/${m.id}`} class="plain grow">
												vs <strong>{opp.username}</strong> — {m.topic_name} (
												{myScore}-{oppScore})
											</a>
											{/* Forfeiting is the only way out of a match whose
											    opponent has stopped answering — a question needs both
											    answers and there is no deadline. It costs the match,
											    so the wording lives in the confirmation. */}
											<button
												type="button"
												class="link muted sm"
												onClick={() =>
													setAbandoning({
														kind: "match",
														id: m.id,
														topic: m.topic_name,
														opponent: opp.username,
													})
												}
											>
												Forfeit
											</button>
										</div>
									)
								})}
							</div>
						)}
					</div>

					<div class="stack gap-2">
						<h3>Recent results</h3>
						{/* This heading used to sit over nothing at all whenever the
						    solo fetch failed. Say so instead, and only claim "no
						    games played yet" once both lists have actually arrived. */}
						{soloError && !solo ? (
							<LoadError message={soloError} onRetry={loadRecentSolo} />
						) : !matches || !solo ? (
							matchesError ? null : (
								<div class="spinner sm" role="status" />
							)
						) : matches.recent.length === 0 && solo.length === 0 ? (
							<p class="muted small">No games played yet.</p>
						) : (
							<div class="list">
								{matches.recent.map((m) => {
									const opp = otherPlayer(m, me.id)
									const myScore = m.scores?.[String(me.id)] ?? 0
									const oppScore = m.scores?.[String(opp.id)] ?? 0
									const outcome =
										m.winner_id === null
											? "Draw"
											: m.winner_id === me.id
												? "Won"
												: "Lost"
									return (
										<a
											href={`/match/${m.id}`}
											class="plain flex between center gap-2"
											key={`m-${m.id}`}
										>
											<span>
												vs <strong>{opp.username}</strong> · {m.topic_name}
											</span>
											<span class="muted small tnum">
												{myScore}-{oppScore} · {outcome}
											</span>
										</a>
									)
								})}
								{solo.map((s) => (
									<a
										href={`/solo/${s.id}`}
										class="plain flex between center gap-2"
										key={`s-${s.id}`}
									>
										<span>Solo · {s.topic_name}</span>
										<span class="muted small tnum">{s.score}/10</span>
									</a>
								))}
							</div>
						)}
					</div>
				</div>

				<SiteFooter />
			</div>

			<Modal
				open={activeTopic !== null}
				onClose={() => setActiveTopic(null)}
				title={
					activeTopic && (
						<span class="flex center gap-2">
							<TopicIcon topic={activeTopic.id} size="1.25rem" />
							{activeTopic.name}
						</span>
					)
				}
			>
				{activeTopic && (
					<div class="stack gap-3">
						<p>{activeTopic?.description}</p>
						<LevelPicker
							levels={activeTopic.levels}
							level={level}
							onChange={setLevel}
						/>
						{/* autofocus so Modal hands focus to the action the dialog
						    exists for, instead of letting <dialog> default it to the
						    close button. */}
						<button
							type="button"
							autofocus
							class="primary"
							disabled={starting}
							onClick={() => playSolo(activeTopic)}
						>
							Play solo
						</button>
						{/* A solo-only topic has no match form yet, and POST /api/matches
						    refuses it. Offering the control anyway would be offering an
						    action that can only fail, so say why instead. */}
						{activeTopic.solo_only ? (
							<p class="muted small">
								This one is solo only for now — it can't be a challenge yet.
							</p>
						) : (
							<div>
								<div class="eyebrow mb-2">Challenge a friend</div>
								{/* "You have no friends yet" is a statement about the
							    account, and it may only be made once the list has
							    actually come back. A failed or in-flight fetch gets
							    its own state — never this sentence. */}
								{!friends && friendsError ? (
									<LoadError message={friendsError} onRetry={loadFriends} />
								) : !friends ? (
									<div class="spinner sm" role="status" />
								) : friends.people.filter((p) => p.status === "friend")
										.length === 0 ? (
									<p class="muted small">
										You have no friends yet. <a href="/friends">Add one</a> to
										start a match.
									</p>
								) : (
									<div class="list">
										{friends.people
											.filter((p) => p.status === "friend")
											.map((f) => {
												// A friend row always has a user; only email
												// invites are null, and those are not friends.
												const user = f.user
												if (!user) return null
												return (
													<button
														type="button"
														key={f.id}
														class="flex center gap-2"
														disabled={starting}
														onClick={() => challengeFriend(activeTopic, user)}
													>
														{user.username}
														<OnlineDot online={f.online} />
													</button>
												)
											})}
									</div>
								)}
							</div>
						)}
					</div>
				)}
			</Modal>

			<ProModal
				open={proReason !== null}
				onClose={() => setProReason(null)}
				reason={proReason || undefined}
			/>

			<Modal
				open={abandoning !== null}
				onClose={() => setAbandoning(null)}
				title={
					abandoning?.kind === "match"
						? "Forfeit this match?"
						: "Abandon round?"
				}
			>
				{abandoning && (
					<div class="stack gap-3">
						{abandoning.kind === "solo" ? (
							<p>
								Your unfinished <strong>{abandoning.topic}</strong> round is
								deleted for good. It counts towards nothing, and there is no way
								to get it back.
							</p>
						) : (
							<p>
								Forfeiting ends the match right now and{" "}
								<strong>{abandoning.opponent} wins it</strong>. It goes down as
								a loss for you and cannot be undone.
							</p>
						)}
						<div class="flex wrap jc-end gap-2">
							<button
								type="button"
								disabled={abandonBusy}
								onClick={() => setAbandoning(null)}
							>
								Keep playing
							</button>
							<button
								type="button"
								class="danger"
								disabled={abandonBusy}
								onClick={confirmAbandon}
							>
								{abandoning.kind === "solo"
									? "Abandon round"
									: `Forfeit — ${abandoning.opponent} wins`}
							</button>
						</div>
					</div>
				)}
			</Modal>
		</div>
	)
}
